import type { ReactElement } from 'react';
import { FEATURES } from './lib/features';
import { BRAND } from './lib/brand';

/**
 * The buggy launcher — everything golf, on the cart screen.
 *
 * Eight tiles rather than eight apps. The head unit is a web surface, so each
 * tile is a route in the build that is already deployed to it: no App Store
 * review, no bundle ids, no eight separate rejection surfaces, and a change
 * reaches every buggy on the course the moment it ships.
 *
 * 🟥 A tile is only drawn if what it opens is actually live.
 *
 * Scoring and handicap posting are behind build flags and OFF in the map
 * build, so hard-coding eight tiles would put two permanently dead buttons on
 * a customer's dashboard — which is precisely the fault App Review rejected
 * the sibling app for. The grid renders what works; turning a flag on adds its
 * tile, and nothing has to be remembered.
 *
 * Sized for a moving buggy: large targets for gloved hands on a bumpy path,
 * dark ground for sun and dusk, and labels that say what the golfer gets
 * rather than what the screen is called.
 */

interface Tile {
  key: string;
  label: string;
  sub: string;
  href: string;
  icon: ReactElement;
  live: boolean;
}

const S = 34;

const IconMap = (
  <svg width={S} height={S} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 3 3 5.5v15L9 18l6 3 6-2.5v-15L15 6 9 3Z" /><path d="M9 3v15" /><path d="M15 6v15" />
  </svg>
);
const IconFlag = (
  <svg width={S} height={S} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 21V4" /><path d="M6 4h11l-2.5 3.5L17 11H6" />
  </svg>
);
const IconSearch = (
  <svg width={S} height={S} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
  </svg>
);
const IconCart = (
  <svg width={S} height={S} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7h11v7H3z" /><path d="M14 10h4l3 3v1h-7" /><circle cx="7" cy="18" r="2" /><circle cx="17" cy="18" r="2" />
  </svg>
);
const IconWeather = (
  <svg width={S} height={S} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 17a4 4 0 0 1 .6-8 5.5 5.5 0 0 1 10.6 1.6A3.5 3.5 0 0 1 17.5 17H7Z" />
  </svg>
);
const IconGauge = (
  <svg width={S} height={S} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 18a8 8 0 1 1 16 0" /><path d="m12 18 4-5" />
  </svg>
);
const IconCard = (
  <svg width={S} height={S} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 10h18" /><path d="M9 10v10" />
  </svg>
);
const IconGear = (
  <svg width={S} height={S} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3.2" /><path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 7.5 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7H1.7a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 3.4 7.5a1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 2.7-1.1V1.7a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 2.7 1.1 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0 1.1 2.7h.1a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.1 1.8Z" />
  </svg>
);
const IconHelp = (
  <svg width={S} height={S} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 1 1 3.3 2.4c-.6.2-.8.7-.8 1.3v.4" /><path d="M12 17h.01" />
  </svg>
);

const TILES: Tile[] = [
  { key: 'map', label: 'Course Map', sub: 'Drive there', href: '/', icon: IconMap, live: true },
  { key: 'hole', label: 'Hole View', sub: 'Yardage to the green', href: '/hole', icon: IconFlag, live: true },
  { key: 'courses', label: 'Courses', sub: '1,449 in Australia', href: '/search', icon: IconSearch, live: true },
  { key: 'yardage', label: 'Yardage Tile', sub: 'Big number', href: '/widget', icon: IconGauge, live: true },
  { key: 'fleet', label: 'Fleet', sub: 'Every buggy out', href: '/map?tab=fleet', icon: IconCart, live: FEATURES.fleet },
  { key: 'weather', label: 'Weather', sub: 'Wind and rain', href: '/weather', icon: IconWeather, live: true },
  { key: 'score', label: 'Scorecard', sub: 'Keep your round', href: '/map?tab=score', icon: IconCard, live: FEATURES.scoring },
  { key: 'setup', label: 'Settings', sub: 'Units and tees', href: '/map?tab=setup', icon: IconGear, live: true },
  { key: 'help', label: 'Help', sub: 'Call the pro shop', href: 'https://gocaddy.mercury-jed.workers.dev/support', icon: IconHelp, live: true },
];

export default function BuggyHome() {
  const tiles = TILES.filter((t) => t.live).slice(0, 8);

  return (
    <div className="buggy">
      <header className="buggy-top">
        <span className="buggy-brand">{BRAND.name}</span>
        <span className="buggy-sub">All things golf, on your buggy</span>
      </header>

      <main className="buggy-grid">
        {tiles.map((t) => (
          <a className="buggy-tile" key={t.key} href={t.href}>
            <span className="buggy-icon">{t.icon}</span>
            <span className="buggy-label">{t.label}</span>
            <span className="buggy-tilesub">{t.sub}</span>
          </a>
        ))}
      </main>
    </div>
  );
}
